import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { guestInfo, guestJoin, reasonOf, whenOf, lengthOf, timeOf } from '../../lib/meetings';
import { setGuestAuth } from '../../lib/sessionLink';
import { remembered, remember, resolve, videoConstraints, isGone } from '../../lib/devices';
import { DevicePanel } from '../CallButton';
import { Icon } from '../todos/icons';
import Logo from '../Logo';
import ThemeToggle from '../ThemeToggle';
import SessionPage from '../../pages/SessionPage';
import './Meetings.css';

/*
 * AN OUTSIDE GUEST'S WAY IN, at /join/:token (Gordon, 22 October).
 *
 * The link in the .ics invitation. No sign-in, no account, no Jarvis
 * session: the token in the address is the whole claim, the page asks for a
 * NAME and nothing else, and "Ask to join" puts them in the waiting room.
 * The server answers with a bearer of the form `guest:<token>`; from then
 * on every session request carries it (lib/sessionLink setGuestAuth) and
 * the ordinary call page runs - SessionPage in GUEST MODE, which means no
 * Recorder, no Add people, no sign-in anywhere, but the chat, the screen,
 * the devices and the recording banner exactly as for everybody else.
 *
 * The phases are the guest's own story: the invitation (name, devices,
 * preview); asking; the call (with the waiting room inside it, drawn by
 * SessionPage); and the end - "Thanks for joining" when it is over or they
 * left, and a plain sentence when the organiser did not let them in. A
 * meeting that has not started yet says so, with the time, and still lets
 * them ask: the organiser lets them in when they open it.
 *
 * Chromeless on purpose: there is nobody signed in to put in a navbar, and
 * a guest should see the firm's name and the meeting's, not a menu of pages
 * they cannot open.
 */

/** Meetings switched off at the firm (078): what a guest is told. */
const SWITCHED_OFF = 'Online meetings are not available at the moment. Please contact the person who invited you.';

export default function GuestJoinPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [devices, setDevices] = useState(remembered);
  const [cameraOn, setCameraOn] = useState(false);
  const [camRefused, setCamRefused] = useState(false);
  const [camNote, setCamNote] = useState(null);
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState(null);
  const [joined, setJoined] = useState(null); // { sessionId, participantId, bearer, name, video }
  const [done, setDone] = useState(null); // 'left' | 'ended' | 'refused'
  const [doneWhy, setDoneWhy] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    let live = true;
    setInfo(null);
    setError(null);
    guestInfo(token).then((got) => {
      if (!live) return;
      setInfo(got);
      if (got?.guest?.name && !name) setName(got.guest.name);
    }).catch((err) => {
      if (!live) return;
      // Meetings switched off at the firm (078): a guest has no administrator
      // to be told about, so the words are for somebody outside NCMB.
      setError(err.response?.data?.switched_off ? SWITCHED_OFF
        : err.response?.status === 410 ? 'This meeting was cancelled.'
        : err.response?.status === 404 ? 'This invitation link is not valid any more. Ask the organiser for a new one.'
          : reasonOf(err, 'Could not read the invitation.'));
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /* The guest bearer goes with this page: set when the server hands it over,
     cleared when the page goes, whatever happened in between. */
  useEffect(() => () => setGuestAuth(null), []);

  const stopCamera = useCallback(() => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    if (!cameraOn || joined) { stopCamera(); return undefined; }
    let stopped = false;
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) { setCamRefused(true); setCameraOn(false); return; }
      const want = resolve('videoinput', null, devices);
      const open = (id) => navigator.mediaDevices.getUserMedia({ video: videoConstraints(id), audio: false });
      try {
        let stream;
        try {
          stream = await open(want.deviceId);
          setCamNote(null);
        } catch (err) {
          if (!want.deviceId || !isGone(err)) throw err;
          setCamNote(`${devices.videoinput?.label ? `"${devices.videoinput.label}"` : 'The remembered camera'} is not plugged in — using the system default.`);
          stream = await open(null);
        }
        if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
        stopCamera();
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (err) {
        if (stopped) return;
        if (err?.name === 'NotAllowedError') setCamRefused(true);
        setCameraOn(false);
      }
    })();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraOn, devices.videoinput?.id, joined]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const ask = async (e) => {
    e?.preventDefault?.();
    const who = name.trim();
    if (!who) { setAskError('Tell them your name first.'); return; }
    setAsking(true);
    setAskError(null);
    try {
      const r = await guestJoin(token, who);
      if (!r?.session_id || !r?.bearer) throw new Error('bad answer');
      setGuestAuth(r.bearer);
      stopCamera();
      // The answer IS the join: me, where the feed is up to, and whether I
      // am at the door. Shaped the way /sessions/:id/join answers, so the
      // call page reads it the same way and asks nothing twice.
      const m = { ...(meeting || {}), ...(r.meeting || {}) };
      setJoined({
        sessionId: r.session_id, participantId: r.participant_id || null, bearer: r.bearer, name: who, video: cameraOn,
        lobby: r.lobby || (info?.lobby === false ? 'admitted' : 'waiting'),
        joined: {
          session: { id: r.session_id, state: 'open', meeting_id: m.id || null, meeting_title: m.title || null, meeting_organiser_name: m.organiser_name || null },
          me: r.me || { id: r.participant_id || null, role: 'user', display_name: who, lobby_state: r.lobby || 'waiting' },
          participants: Array.isArray(r.participants) ? r.participants : [],
          cursor: r.cursor || '0', chat_cursor: r.chat_cursor || '0', lobby: r.lobby || 'waiting'
        }
      });
    } catch (err) {
      const status = err.response?.status;
      const d = err.response?.data || {};
      if (status === 403 && d.lobby === 'refused') { finished('refused'); return; }
      setAskError(d.switched_off ? SWITCHED_OFF
        : status === 410 || d.cancelled ? 'This meeting was cancelled.'
        : status === 409 && d.ended ? 'This meeting has ended.'
          : status === 409 || status === 425 ? 'The organiser has not started the meeting yet. Try again in a moment.'
            : reasonOf(err, 'Could not ask to join. Try again.'));
    } finally {
      setAsking(false);
    }
  };

  const finished = (why, detail) => {
    setGuestAuth(null);
    setJoined(null);
    setDone(why);
    setDoneWhy(detail || null);
  };

  const meeting = info?.meeting || null;
  const notStarted = meeting && meeting.state === 'scheduled'
    && new Date(meeting.starts_at).getTime() - Date.now() > 15 * 60000;

  /* ------------------------------------------------------------ the call */
  if (joined) {
    return (
      <SessionPage guest={{
        token, sessionId: joined.sessionId, participantId: joined.participantId, name: joined.name,
        lobby: joined.lobby !== 'admitted', video: joined.video, joined: joined.joined,
        meeting: { id: meeting?.id, title: meeting?.title, organiser_name: meeting?.organiser_name },
        onDone: finished
      }} />
    );
  }

  const shell = (children) => (
    <div className="mt-guest" data-phase={done || (meeting ? 'invite' : error ? 'error' : 'loading')}>
      <header className="mt-guest-head">
        <span className="mt-guest-logo"><Logo /></span>
        <span className="mt-guest-firm">NCMB · a Jarvis meeting</span>
        <span className="mt-guest-theme"><ThemeToggle /></span>
      </header>
      <main className="mt-guest-main">{children}</main>
    </div>
  );

  /* --------------------------------------------------------------- done */
  if (done) {
    const refused = done === 'refused';
    return shell(
      <section className={`mt-guest-card mt-guest-done${refused ? ' is-refused' : ''}`} role="status" data-done={done}>
        <span className="mt-guest-mark" aria-hidden="true"><Icon name={refused ? 'close' : 'check'} size={28} /></span>
        <h1 className="mt-guest-title">{refused ? 'The organiser did not admit you' : 'Thanks for joining'}</h1>
        <p className="mt-quiet">
          {refused
            ? 'You were not let into this meeting. If you think that is a mistake, get in touch with whoever sent you the invitation.'
            : done === 'ended'
              ? `The meeting has ended${doneWhy ? ` — ${doneWhy}` : ''}. You can close this page.`
              : 'You left the meeting. You can close this page, or ask to join again if you left by mistake.'}
        </p>
        {!refused && meeting && <p className="mt-guest-meeting-name">{meeting.title}</p>}
        {done === 'left' && (
          <button type="button" className="btn btn-outline tds-btn mt-guest-again" onClick={() => { setDone(null); setDoneWhy(null); }}>
            Ask to join again
          </button>
        )}
      </section>
    );
  }

  /* -------------------------------------------------------------- error */
  if (error) {
    return shell(
      <section className="mt-guest-card" role="alert">
        <h1 className="mt-guest-title">This link does not work</h1>
        <p className="mt-quiet">{error}</p>
      </section>
    );
  }

  if (!meeting) return shell(<p className="mt-quiet mt-guest-loading">Reading your invitation…</p>);

  const over = meeting.state === 'ended' || meeting.state === 'cancelled';

  /* --------------------------------------------------------- invitation */
  return shell(
    <section className="mt-guest-card mt-guest-invite" aria-label="Your invitation">
      <div className="mt-guest-about">
        <h1 className="mt-guest-title">{meeting.title || 'A meeting'}</h1>
        <p className="mt-guest-when">
          <Icon name="calendar" size={14} />
          <time dateTime={meeting.starts_at || undefined}>{whenOf(meeting.starts_at, meeting.ends_at)}</time>
          {meeting.ends_at && <><span className="mt-sep">·</span>{lengthOf(meeting.starts_at, meeting.ends_at)}</>}
          <span className="mt-sep">·</span>Malta time
        </p>
        {meeting.organiser_name && (
          <p className="mt-quiet">Organised by <strong>{meeting.organiser_name}</strong>{info?.guest?.name ? `, who invited ${info.guest.name}.` : '.'}</p>
        )}
        {meeting.agenda && (
          <details className="mt-agenda mt-guest-agenda">
            <summary><Icon name="list" size={14} /> Agenda</summary>
            <ol className="mt-agenda-list">
              {String(meeting.agenda).split(/\r?\n/).filter((l) => l.trim()).map((l, i) => <li key={i}>{l.replace(/^\s*(\d+[.)]|[-*•])\s*/, '')}</li>)}
            </ol>
          </details>
        )}
        {over && (
          <p className="mt-guest-note is-over" role="status">
            {meeting.state === 'cancelled' ? 'This meeting was cancelled.' : 'This meeting has ended.'}
          </p>
        )}
        {!over && notStarted && (
          <p className="mt-guest-note" role="status" data-not-started="">
            <Icon name="clock" size={14} />
            <span>
              It starts at <strong>{timeOf(meeting.starts_at)}</strong> and the organiser hasn&rsquo;t started yet; you&rsquo;ll be
              let in when they do.
            </span>
          </p>
        )}
        {!over && meeting.state === 'live' && (
          <p className="mt-guest-note is-live" role="status"><span className="mt-live-dot" aria-hidden="true" /><span>The meeting is on now.</span></p>
        )}
      </div>

      {!over && (
        <form className="mt-guest-form" onSubmit={ask} noValidate>
          <label className="tds-field mt-guest-name">
            <span>Your name</span>
            <input type="text" value={name} maxLength={80} autoFocus={!name} placeholder="So they know who is asking"
              aria-label="Your name" onChange={(e) => setName(e.target.value)} />
          </label>

          <div className="mt-prejoin mt-guest-prejoin" data-camera={cameraOn ? 'on' : 'off'}>
            <div className="mt-preview">
              <video ref={videoRef} className="mt-preview-video" autoPlay playsInline muted data-live={cameraOn || undefined} />
              {!cameraOn && (
                <div className="mt-preview-off">
                  <Icon name="person" size={34} />
                  <span>{camRefused ? 'Your camera is blocked in this browser' : 'Your camera is off'}</span>
                </div>
              )}
              <div className="mt-preview-bar">
                <button type="button" className="se-flat mt-preview-toggle" aria-pressed={cameraOn} onClick={() => setCameraOn((v) => !v)}>
                  {cameraOn ? 'Camera off' : 'Camera on'}
                </button>
                {camNote && <span className="mt-preview-note">{camNote}</span>}
              </div>
            </div>
            <div className="mt-prejoin-side">
              <DevicePanel choice={devices} onChoose={(kind, d) => setDevices(remember(kind, d))} idPrefix="mt-guest-dev" />
            </div>
          </div>

          {askError && <p className="mt-error" role="alert">{askError}</p>}
          <div className="mt-guest-foot">
            <button type="submit" className="btn btn-primary tds-btn mt-ask-join" disabled={asking}>
              {asking ? 'Asking…' : 'Ask to join'}
            </button>
            <span className="mt-quiet">
              {info?.lobby === false ? 'You will go straight into the meeting.'
                : 'Somebody in the meeting lets you in. Nothing is recorded without a banner saying so.'}
            </span>
          </div>
        </form>
      )}
    </section>
  );
}
